import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "../api/client.js";
import OfficerSelect from "./OfficerSelect.jsx";

/**
 * ZoneOfficerAssignModal
 *
 * Opens a modal to assign (or reassign) an officer to a specific zone-link
 * in a COMBINED para.  The backend enforces that the officer must belong to
 * that zone, so we filter locally for a nicer UX and let the server validate.
 *
 * Props:
 *  zoneLink  – the zone_links entry (has .id, .zone, .zone_name, .officer, .officer_name)
 *  onClose   – callback to close the modal
 *  onDone    – callback called after successful assignment (triggers a reload)
 */
export default function ZoneOfficerAssignModal({ zoneLink, onClose, onDone }) {
  const [officers, setOfficers] = useState([]);
  const [selected, setSelected] = useState(String(zoneLink.officer || ""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isEdit = !!zoneLink.officer;

  useEffect(() => {
    // Fetch officers posted in this zone only
    api
      .get("/auth/users/", {
        params: { zone: zoneLink.zone, role: "OFFICER", page_size: 200 },
      })
      .then((r) => setOfficers(r.data.results || r.data))
      .catch(() => setError("Could not load officers for this zone."));
  }, [zoneLink.zone]);

  const submit = async () => {
    if (!selected) {
      setError("Please select an officer.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      await api.post(`/para-zones/${zoneLink.id}/assign_officer/`, {
        officer: Number(selected),
      });
      onDone();
    } catch (e) {
      setError(
        e.response?.data?.officer ||
          e.response?.data?.detail ||
          "Failed to assign officer."
      );
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <h3 className="modal-title" style={{ marginBottom: "16px" }}>
          {isEdit ? "Edit Officer Assignment" : "Assign Officer"}
        </h3>

        {/* Info Box based on mockup design */}
        <div style={{ display: "flex", gap: "8px", padding: "0 0 16px 0" }}>
          <div style={{ flex: 1, background: "#f0f9ff", border: "1px solid #bfdbfe", borderRadius: "8px", padding: "9px 11px" }}>
            <div style={{ fontSize: "10px", fontWeight: "700", color: "#172554", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: "3px" }}>
              Zone
            </div>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#1e293b" }}>
              {zoneLink.zone_name}
            </div>
          </div>
          {isEdit && (
            <div style={{ flex: 1, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "9px 11px" }}>
              <div style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: "3px" }}>
                Current Officer
              </div>
              <div style={{ fontSize: "12px", fontWeight: "600", color: "#1e293b" }}>
                {zoneLink.officer_name}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div
            className="error-text"
            style={{ marginBottom: "1rem", color: "#dc2626", fontSize: "12px", fontWeight: "500", background: "#fef2f2", border: "1px solid #fca5a5", padding: "8px 10px", borderRadius: "6px" }}
          >
            {error}
          </div>
        )}

        {/* Officer search */}
        <div className="field">
          <label>
            {isEdit ? "Select New Officer" : "Select Officer"}
          </label>
          <div className="muted" style={{ fontSize: 11.5, marginBottom: 8, color: "#64748b" }}>
            Showing officers posted in <strong>{zoneLink.zone_name}</strong> only.
          </div>
          <OfficerSelect
            officers={officers}
            value={selected}
            onChange={setSelected}
          />
        </div>

        {/* Footer actions */}
        <div className="modal-actions" style={{ marginTop: "18px", display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <button className="btn-outline" onClick={onClose} style={{ padding: "7px 14px", borderRadius: "7px", fontSize: "11.5px", fontWeight: "500", cursor: "pointer", border: "1px solid #cbd5e1", background: "#fff", color: "#334155" }}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy || !selected}
            onClick={submit}
            style={{ padding: "7px 14px", borderRadius: "7px", fontSize: "11.5px", fontWeight: "600", cursor: "pointer", border: "none", background: "#1e3a8a", color: "#fff", opacity: (busy || !selected) ? 0.6 : 1 }}
          >
            {busy ? "Saving…" : isEdit ? "Update Officer" : "Assign Officer"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
